/**
 * ScopedWatchlistPickerView - Bottom-sheet watchlist picker scoped to player detail
 *
 * Renders a watchlist selection UI as a bottom sheet portaled into
 * PLAYER_DETAIL_PORTAL so the overlay stays within the player detail panel
 * instead of covering the full viewport like AddToWatchlistActionsheet.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { Portal } from "@gorhom/portal";
import { PlusIcon } from "lucide-react-native";
import React, { memo } from "react";
import { type ViewStyle } from "react-native";

import CustomPromptContainer, {
  CustomPromptRefProps,
} from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import WatchlistAddRemoveButton from "@/components/molecules/WatchlistAddRemoveButton";
// design-system: compound waiver — non-collapsible button composition
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: unavoidable primitive usage outside atoms (@/components/ui import(s): @/components/ui/button; react-native primitive(s):…
import { Button, ButtonIcon, ButtonText } from "@/components/ui/button";
import { Box } from "@/components/atoms/Box";
import { Divider } from "@/components/atoms/Divider";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import {
  PlayerSearchPlayerFragment,
  WatchlistWatchlistMetaFragment,
} from "@/generated/graphql";
import { RADIUS } from "@/config/radius";

/**
 * Portal name for the player detail content area — must match the
 * PortalHost rendered in PlayerDetailCenterView
 */
const PLAYER_DETAIL_PORTAL_NAME = "player-detail-portal";

/**
 * Get divider class based on theme
 * @param isDark - Whether dark mode is active
 */
const getDividerClass = (isDark: boolean): string =>
  isDark ? "border-outline-emphasis" : "border-outline-strong";

/**
 * Get sheet background color based on theme
 * @param isDark - Whether dark mode is active
 */
const getSheetBg = (isDark: boolean): string =>
  isDark ? "#18181b" : "#ffffff";

/**
 * Get sheet border color based on theme
 * @param isDark - Whether dark mode is active
 */
const getSheetBorder = (isDark: boolean): string =>
  isDark ? "#3f3f46" : "#e4e4e7";

/**
 * Bottom-sheet container style — theme-driven surface color/border, 16px
 * top-only corner radius, and a 70% max-height, none of which have closed
 * design tokens. The static bottom padding moves to the className (`pb-6`).
 * design-system: dynamic-style waiver — data-driven value (theme palette)
 * @param isDark - Whether dark mode is active
 */
const sheetStyle = (isDark: boolean): ViewStyle => ({
  backgroundColor: getSheetBg(isDark),
  borderTopLeftRadius: RADIUS.xl,
  borderTopRightRadius: RADIUS.xl,
  borderTopWidth: 1,
  borderColor: getSheetBorder(isDark),
  maxHeight: "70%",
});

/**
 * Drag-handle pill style — bespoke 5px height and 3px corner radius (off the
 * closed sizing/radius ladders), a theme-driven fill, and its own vertical
 * offset (the no-margin className rule forbids margin utilities, so the offset
 * stays inline). Alignment and width move to the className (`self-center w-9`).
 * design-system: dynamic-style waiver — off-token handle geometry + theme color
 * @param isDark - Whether dark mode is active
 */
const dragHandleStyle = (isDark: boolean): ViewStyle => ({
  height: 5,
  borderRadius: RADIUS.full,
  backgroundColor: isDark ? "#52525b" : "#d4d4d8",
  marginTop: 8,
  marginBottom: 12,
});

/**
 * Stops event propagation to prevent backdrop close when pressing sheet content
 * @param e - Press event with stopPropagation method
 * @param e.stopPropagation - Function to stop event bubbling
 */
const handleStopPropagation = (e: { stopPropagation: () => void }): void => {
  e.stopPropagation();
};

/**
 * Props for ScopedWatchlistPickerView
 */
export interface ScopedWatchlistPickerViewProps {
  /** Player data to add to watchlists */
  readonly player: PlayerSearchPlayerFragment;
  /** Whether the picker is open */
  readonly isOpen: boolean;
  /** Callback when the picker should close */
  readonly onClose: () => void;
  /** Available watchlists to display */
  readonly watchlists: readonly WatchlistWatchlistMetaFragment[];
  /** Callback to create a new watchlist */
  readonly onAddWatchlistPress: () => void;
  /** Whether a watchlist operation is in progress */
  readonly loading: boolean;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Ref for the custom prompt dialog used by "+ New List" */
  readonly promptRef: React.RefObject<CustomPromptRefProps | null>;
}

/**
 * ScopedWatchlistPickerView renders a bottom-sheet watchlist picker portaled
 * into the player detail content area via PLAYER_DETAIL_PORTAL
 * @param props - View props for the scoped watchlist picker
 * @param props.player - Player data
 * @param props.isOpen - Whether the picker is open
 * @param props.onClose - Close handler
 * @param props.watchlists - Available watchlists
 * @param props.onAddWatchlistPress - New list handler
 * @param props.loading - Whether loading
 * @param props.isDark - Whether dark mode is active
 * @param props.promptRef - Ref for the custom prompt dialog used by "+ New List"
 * @returns Portal-rendered bottom sheet, or null when closed
 */
const ScopedWatchlistPickerView = ({
  player,
  isOpen,
  onClose,
  watchlists,
  onAddWatchlistPress,
  loading,
  isDark,
  promptRef,
}: ScopedWatchlistPickerViewProps) => (
  <>
    <CustomPromptContainer ref={promptRef} />
    {isOpen ? (
      <Portal hostName={PLAYER_DETAIL_PORTAL_NAME}>
        {/* Backdrop — covers only the player detail area */}
        <Pressable
          className="absolute inset-0 justify-end"
          // eslint-disable-next-line no-restricted-syntax -- bespoke 40% black modal scrim; no scrim color token in the closed vocabulary
          UNSAFE_style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Close watchlist picker"
        >
          {/* Sheet — anchored to bottom of player detail area */}
          <Pressable
            onPress={handleStopPropagation}
            className="pb-6"
            // eslint-disable-next-line no-restricted-syntax -- theme-driven sheet surface (hex palette + 70% max-height); no closed tokens
            UNSAFE_style={sheetStyle(isDark)}
          >
            {/* Drag handle */}
            <Box
              className="w-9 self-center"
              // eslint-disable-next-line no-restricted-syntax -- bespoke drag-handle pill (5px height, own vertical margin) + theme-driven fill; no closed tokens
              UNSAFE_style={
                dragHandleStyle(isDark) as Record<string, string | number>
              }
            />

            {/* Header */}
            <Stack
              direction="horizontal"
              className="w-full items-center justify-center px-4 pb-2"
            >
              <Box className="w-12" />
              <Box className="flex-1">
                <Text
                  variant="title"
                  className={`text-center ${isDark ? "text-white" : "text-content-primary"}`}
                >
                  Add to List
                </Text>
              </Box>
              <Button variant="link" onPress={onClose}>
                <ButtonText className="text-title-sm text-status-info">
                  Done
                </ButtonText>
              </Button>
            </Stack>

            <Text
              variant="unstyled"
              className={`pb-4 text-center font-body text-title-sm ${isDark ? "text-content-subtle" : "text-content-neutral-strong"}`}
            >
              Choose which watchlists {player?.name} appears in.
            </Text>

            {/* Watchlist items */}
            <ScrollView
              className="max-h-72 px-4"
              contentContainerClassName={`rounded-md py-4 ${isDark ? "bg-surface-action" : "bg-surface-subtle"}`}
              keyboardShouldPersistTaps="handled"
            >
              <Stack>
                {watchlists.map((watchlist, index) => (
                  <React.Fragment key={watchlist.id}>
                    {index > 0 ? (
                      <Stack className="py-2">
                        <Divider className={getDividerClass(isDark)} />
                      </Stack>
                    ) : null}
                    <WatchlistAddRemoveButton
                      player={player}
                      watchlist={watchlist}
                    />
                  </React.Fragment>
                ))}
              </Stack>
            </ScrollView>

            {/* New list button */}
            <Box className="px-4 pt-4">
              <Button
                isDisabled={loading}
                variant="solid"
                onPress={onAddWatchlistPress}
                className={`rounded-sm ${isDark ? "bg-surface-action" : "bg-surface-subtle"}`}
              >
                <ButtonIcon
                  className="text-status-info"
                  as={PlusIcon}
                  size="md"
                />
                <ButtonText className="font-body text-title-sm text-status-info">
                  {loading ? "Please Wait..." : "New List"}
                </ButtonText>
              </Button>
            </Box>
          </Pressable>
        </Pressable>
      </Portal>
    ) : null}
  </>
);

ScopedWatchlistPickerView.displayName = "ScopedWatchlistPickerView";

export default memo(ScopedWatchlistPickerView);
